import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useAppContext } from "@/contexts/AppContext";
import {
  clearanceOriginal,
  extractSpecSheet,
  formatClearanceOriginal,
  type ConsumerSpecs,
  type InstallerSpecs,
} from "@/lib/specReader";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import ClearanceReextractDialog from "@/components/ClearanceReextractDialog";
import { diffClearances, mergeClearances } from "@/lib/clearanceMerge";
import { arDisplayStatus } from "@/lib/assetStatus";
import {
  assemblyRerun,
  finishOptionLabel,
  finishPatch,
  swatchModeWarning,
  type FinishOption,
} from "@/lib/finishSelection";

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Duplicated from fyrris-mobile/constants/usStates.ts — see that file's comment for why
// this isn't reused from territory_pricing_tiers. Small and static enough that keeping
// two copies is simpler than a shared cross-repo package.
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

const CATEGORY_GROUPS = [
  {
    label: "Hearth",
    options: [
      { value: "gas_fireplace",       label: "Gas Fireplace / Insert" },
      { value: "wood_fireplace",      label: "Wood Fireplace / Stove" },
      { value: "pellet_stove",        label: "Pellet Stove / Insert" },
      { value: "electric_fireplace",  label: "Electric Fireplace" },
      { value: "fireplace_insert",    label: "Fireplace Insert" },
      { value: "gas_logs",            label: "Gas Logs & Burners" },
      { value: "chimney_venting",     label: "Chimney & Venting" },
      { value: "fireplace_accessory", label: "Hearth Accessory" },
    ],
  },
  {
    label: "Patio & Outdoor",
    options: [
      { value: "patio_furniture",   label: "Patio Furniture" },
      { value: "outdoor_kitchen",   label: "Outdoor Kitchen" },
      { value: "pergola_gazebo",    label: "Pergola / Gazebo" },
      { value: "fire_pit",          label: "Fire Pit / Bowl" },
      { value: "patio_heater",      label: "Patio Heater" },
      { value: "outdoor_accessory", label: "Outdoor Accessory" },
    ],
  },
  {
    label: "Barbecue",
    options: [
      { value: "gas_grill",      label: "Gas Grill" },
      { value: "charcoal_grill", label: "Charcoal Grill" },
      { value: "pellet_grill",   label: "Pellet Grill / Smoker" },
      { value: "kamado_grill",   label: "Kamado Grill" },
      { value: "built_in_grill", label: "Built-in Grill" },
      { value: "bbq_accessory",  label: "BBQ Accessory" },
    ],
  },
] as const;

// Keyed on ArDisplayStatus, not raw asset_status. `arStatus` in this file is
// always the normalised value now — see src/lib/assetStatus.ts.
const AR_STATUS_META: Record<string, { label: string; classes: string }> = {
  ready:      { label: "AR Ready",   classes: "bg-green-900/40 text-green-400 border-green-800" },
  processing: { label: "Processing", classes: "bg-blue-900/40 text-blue-400 border-blue-800" },
  pending:    { label: "Pending",    classes: "bg-yellow-900/40 text-yellow-400 border-yellow-800" },
  failed:     { label: "Failed",     classes: "bg-red-900/40 text-red-400 border-red-800" },
};

const TEXTURE_MAPS = [
  { key: "diffuse"   as const, label: "Diffuse Map",   tip: "Color and pattern of the surface" },
  { key: "normal"    as const, label: "Normal Map",    tip: "Encodes surface bumps and fine detail" },
  { key: "roughness" as const, label: "Roughness Map", tip: "Controls shiny vs matte appearance" },
  { key: "metalness" as const, label: "Metalness Map", tip: "Marks metallic (white) vs non-metallic (black) areas" },
] as const;
type TextureKey = (typeof TEXTURE_MAPS)[number]["key"];

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface Manufacturer { id: string; name: string; }

interface ProductData {
  id: string;
  name: string;
  category: string | null;
  manufacturer_id: string | null;
  active: boolean;
  base_price: number | null;
  description: string | null;
  image_url: string | null;
  asset_status: string | null;
  pbr_status: string | null;
  geometry_status: string | null;
  glb_assembly_status: string | null;
  qa_status: string | null;
  qa_flags: string[] | null;
  qa_render_url: string | null;
  glb_pbr_url: string | null;
  usdz_pbr_url: string | null;
  spec_sheet_url: string | null;
  consumer_specs_json: Record<string, unknown> | null;
  install_specs_json: Record<string, unknown> | null;
  // products.clearances is a JSONB document, not a flat number map: it also holds
  // "original" and per-standard "standards" sets. Typed loosely on purpose.
  clearances: Record<string, unknown> | null;
  flame_anchor: { x: number; y: number; z: number; scale: number } | null;
  available_countries: string[] | null;
  // Parametric / placement (M2). placement_type defaults to 'wall' and
  // geometry_source to null for every pre-existing photogrammetry product.
  placement_type: string | null;
  geometry_source: string | null;
  material_source: string | null;
  finish_id: string | null;
  use_photo_texture: boolean | null;
  footprint_width_mm: number | null;
  footprint_depth_mm: number | null;
  overall_height_mm: number | null;
  requires_open_sky: boolean | null;
  shadow_atlas_key: string | null;
  created_at: string;
  updated_at: string | null;
  manufacturers?: { name: string } | null;
}

/* ------------------------------------------------------------------ */
/* Network helpers                                                      */
/* ------------------------------------------------------------------ */

function xhrPut(url: string, file: File | Blob, contentType: string, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { onProgress(100); resolve(); }
      else reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText?.slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(file);
  });
}

async function patchProduct(id: string, fields: Record<string, unknown>, jwt: string): Promise<void> {
  const res = await fetch(`${SB_URL}/rest/v1/products?id=eq.${id}`, {
    method: "PATCH",
    headers: {
      apikey: ANON_KEY,
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
      Prefer: "return=minimal",
    },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Save failed (${res.status}): ${text.slice(0, 200)}`);
  }
}

async function getWizardUploadUrl(s3Key: string, contentType: string, jwt: string): Promise<{ uploadUrl: string; publicUrl: string }> {
  const res = await fetch(`${SB_URL}/functions/v1/get-wizard-upload-url`, {
    method: "POST",
    headers: {
      apikey: ANON_KEY,
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ s3_key: s3Key, content_type: contentType }),
  });
  const text = await res.text();
  if (!res.ok) throw new Error(`Upload URL failed (${res.status}): ${text.slice(0, 200)}`);
  const { upload_url, public_url } = JSON.parse(text);
  return { uploadUrl: upload_url, publicUrl: public_url };
}

/* ------------------------------------------------------------------ */
/* Shared UI                                                            */
/* ------------------------------------------------------------------ */

const inputStyle = { background: "#111827", color: "#f9fafb" };
const inputCls = "w-full rounded-lg border border-gray-700 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none";

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">{children}</label>;
}

function Section({
  title, open, onToggle, children,
}: {
  title: string; open: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-800 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-800/30 transition"
      >
        <span className="font-medium text-gray-100">{title}</span>
        <span className="text-gray-500 text-sm">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-gray-800 space-y-4">{children}</div>}
    </div>
  );
}

function SaveButton({ saving, onClick }: { saving: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
    >
      {saving ? "Saving…" : "Save changes"}
    </button>
  );
}

function ArStatusBadge({ status }: { status: string | null }) {
  const meta = AR_STATUS_META[status ?? ""] ?? { label: "No AR", classes: "bg-gray-800 text-gray-500 border-gray-700" };
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.classes}`}>{meta.label}</span>;
}

function CopyableUrl({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  const masked = url.length > 60 ? url.slice(0, 55) + "…" : url;
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-2">
      <div>
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        <p className="text-xs text-gray-400 font-mono break-all">{masked}</p>
      </div>
      <button
        onClick={copy}
        className="shrink-0 rounded border border-gray-700 px-2 py-1 text-xs text-gray-400 hover:text-gray-200 transition"
      >
        {copied ? "✓" : "Copy"}
      </button>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-8 w-48 rounded bg-gray-800" />
      <div className="h-4 w-32 rounded bg-gray-800" />
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-16 rounded-2xl bg-gray-800" />
      ))}
    </div>
  );
}

function StateRestrictionsSection({ productId, jwt }: { productId: string; jwt: string }) {
  const { toast } = useToast();
  const [restrictions, setRestrictions] = useState<Record<string, string>>({}); // state_code -> reason
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${SB_URL}/rest/v1/product_state_availability?product_id=eq.${productId}` +
            `&is_available=eq.false&select=state_code,restriction_reason`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
        );
        if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
        const rows: { state_code: string; restriction_reason: string | null }[] = await res.json();
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const row of rows) next[row.state_code] = row.restriction_reason ?? "";
        setRestrictions(next);
      } catch (err) {
        if (!cancelled) {
          toast({ title: "Failed to load state restrictions", description: err instanceof Error ? err.message : "Unknown error" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId, jwt]);

  async function toggleState(code: string) {
    const wasRestricted = code in restrictions;
    setPending((p) => new Set(p).add(code));
    try {
      if (wasRestricted) {
        // Prefer: return=representation (not return=minimal) is deliberate here — under RLS,
        // PostgREST's USING clause on UPDATE/DELETE silently filters out rows the caller can't
        // touch and still answers 200/204 with zero rows affected. Checking res.ok alone would
        // treat that silent no-op as success. Requesting the deleted rows back and checking the
        // array is non-empty is the only way to tell "actually deleted" from "RLS filtered this
        // out and nothing happened."
        const res = await fetch(
          `${SB_URL}/rest/v1/product_state_availability?product_id=eq.${productId}&state_code=eq.${code}`,
          {
            method: "DELETE",
            headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, Prefer: "return=representation" },
          }
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Remove restriction failed (${res.status}): ${text.slice(0, 200)}`);
        }
        const deleted: unknown[] = await res.json();
        if (deleted.length === 0) {
          throw new Error("Remove restriction failed: no row was deleted (permission denied or already removed)");
        }
        // Only reflect the change locally once the delete is confirmed — otherwise a failed
        // write (RLS denial, network error) would leave the checkbox unchecked while the row
        // still exists in the database.
        setRestrictions((prev) => {
          const next = { ...prev };
          delete next[code];
          return next;
        });
      } else {
        const res = await fetch(`${SB_URL}/rest/v1/product_state_availability`, {
          method: "POST",
          headers: {
            apikey: ANON_KEY,
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
            Prefer: "resolution=merge-duplicates,return=representation",
          },
          body: JSON.stringify({ product_id: productId, state_code: code, is_available: false, restriction_reason: "" }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Add restriction failed (${res.status}): ${text.slice(0, 200)}`);
        }
        const inserted: unknown[] = await res.json();
        if (inserted.length === 0) {
          throw new Error("Add restriction failed: no row was written (permission denied)");
        }
        setRestrictions((prev) => ({ ...prev, [code]: "" }));
      }
    } catch (err) {
      toast({ title: "Failed to update state restriction", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setPending((p) => { const next = new Set(p); next.delete(code); return next; });
    }
  }

  async function updateReason(code: string, reason: string) {
    const previous = restrictions[code] ?? "";
    // Optimistic — keeps typing responsive — but reverted below if the write fails.
    setRestrictions((prev) => ({ ...prev, [code]: reason }));
    try {
      // See the DELETE branch of toggleState for why return=representation (not
      // return=minimal) is required to detect an RLS-filtered zero-row no-op.
      const res = await fetch(
        `${SB_URL}/rest/v1/product_state_availability?product_id=eq.${productId}&state_code=eq.${code}`,
        {
          method: "PATCH",
          headers: {
            apikey: ANON_KEY,
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
            Prefer: "return=representation",
          },
          body: JSON.stringify({ restriction_reason: reason }),
        }
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Save reason failed (${res.status}): ${text.slice(0, 200)}`);
      }
      const updated: unknown[] = await res.json();
      if (updated.length === 0) {
        throw new Error("Save reason failed: no row was updated (permission denied or row missing)");
      }
    } catch (err) {
      setRestrictions((prev) => ({ ...prev, [code]: previous }));
      toast({ title: "Failed to save restriction reason", description: err instanceof Error ? err.message : "Unknown error" });
    }
  }

  if (loading) return <p className="text-xs text-gray-600">Loading state restrictions…</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        All states are available by default. Check a state to mark this product unavailable there.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
        {US_STATES.map((s) => {
          const restricted = s.code in restrictions;
          return (
            <div key={s.code} className="space-y-1">
              <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={restricted}
                  disabled={pending.has(s.code)}
                  onChange={() => toggleState(s.code)}
                  className="rounded"
                />
                {s.name}
              </label>
              {restricted && (
                <input
                  type="text"
                  value={restrictions[s.code]}
                  onChange={(e) => updateReason(s.code, e.target.value)}
                  placeholder="Reason (optional, internal)"
                  style={inputStyle}
                  className={inputCls + " text-xs py-1"}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */

/**
 * Read-only summary of a parametrically generated product.
 *
 * A parametric product has no source photos and no uploaded mesh, so the
 * upload-oriented sections have nothing to say about it. This is what actually
 * determined its geometry — the numbers an admin needs to sanity-check a
 * generated pergola without opening the database.
 *
 * Read-only on purpose: editing a dimension here would desynchronise the
 * product from the mesh and shadow atlas already baked from it. Regeneration
 * goes through the draft → approve path, which rebuilds all three together.
 */
function ParametricSummary({ product }: { product: ProductData | null }) {
  if (!product) return null;

  const mm = (v: number | null) => (v == null ? "—" : `${v} mm`);
  const rows: [string, React.ReactNode][] = [
    ["Geometry source", product.geometry_source ?? "—"],
    ["Material source", product.material_source ?? "—"],
    ["Placement type", product.placement_type ?? "wall"],
    ["Footprint", product.footprint_width_mm == null && product.footprint_depth_mm == null
      ? "—"
      : `${mm(product.footprint_width_mm)} × ${mm(product.footprint_depth_mm)}`],
    ["Overall height", mm(product.overall_height_mm)],
    ["Requires open sky", product.requires_open_sky ? "Yes" : "No"],
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Generated from parameters — there are no source photos or uploaded mesh to manage.
        Values are read-only; regenerate through the parametric draft approval flow so the
        mesh and shadow atlas stay in step with them.
      </p>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-gray-500">{label}</dt>
            <dd className="text-gray-200">{value}</dd>
          </div>
        ))}
      </dl>

      <div>
        <p className="text-sm text-gray-500 mb-1">Shadow atlas</p>
        {product.shadow_atlas_key ? (
          <a
            href={`${product.shadow_atlas_key}index.json`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 text-sm underline break-all"
          >
            {product.shadow_atlas_key}index.json
          </a>
        ) : (
          <p className="text-sm text-gray-600">
            Not baked. The atlas is written during parametric approval — a product approved
            before that step existed will not have one until it is re-approved.
          </p>
        )}
      </div>
    </div>
  );
}

export default function AdminProductEdit() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { session } = useAuth();
  const navigate = useNavigate();

  const jwt = session?.access_token ?? "";

  const [product, setProduct] = useState<ProductData | null>(null);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Section open state
  const [openSections, setOpenSections] = useState({
    info: true, media: false, ar: false, specs: false, clearances: false, flameAnchor: false,
    stateRestrictions: false, parametric: false, danger: false,
  });
  function toggleSection(key: keyof typeof openSections) {
    setOpenSections((s) => ({ ...s, [key]: !s[key] }));
  }

  // Which surfaces apply to this product. Both default to the photogrammetry
  // answer for every pre-existing row (geometry_source null, placement_type
  // 'wall'), so nothing about the shipped hearth flow changes.
  const isParametric = product?.geometry_source != null && product.geometry_source !== "photogrammetry";
  const isGroundStructure = product?.placement_type === "ground_staked";

  // Section 1 — Product Info
  const [infoForm, setInfoForm] = useState({
    name: "", category: "", manufacturer_id: "", base_price: "", description: "", active: true,
    available_countries: null as string[] | null,
  });
  const [savingInfo, setSavingInfo] = useState(false);

  // Section 2 — Media
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroProgress, setHeroProgress] = useState(0);
  const [textureUploading, setTextureUploading] = useState<TextureKey | null>(null);
  const heroRef = useRef<HTMLInputElement>(null);
  const textureRefs = useRef<Partial<Record<TextureKey, HTMLInputElement | null>>>({});

  // Section 3 — AR / Pipeline
  const [arStatus, setArStatus] = useState<string | null>(null);
  const [retriggeringPbr, setRetriggeringPbr] = useState(false);
  const [retriggeringGeo, setRetriggeringGeo] = useState(false);
  const [retriggeringFull, setRetriggeringFull] = useState(false);
  // Admin finish selector + assembly re-run
  const { isAdmin } = useAppContext();
  const [finishes, setFinishes] = useState<FinishOption[]>([]);
  const [savingFinish, setSavingFinish] = useState(false);
  const [showAssemblyConfirm, setShowAssemblyConfirm] = useState(false);
  const [reassembling, setReassembling] = useState(false);
  // Demo link modal (sub-feature of Section 3)
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoViewCount, setDemoViewCount] = useState<number | null>(null);
  const [demoCopied, setDemoCopied] = useState(false);

  // Section 4 — Spec Sheet
  const [specUploading, setSpecUploading] = useState(false);
  const [specProgress, setSpecProgress] = useState(0);
  const [specExtracting, setSpecExtracting] = useState(false);
  // Extraction result awaiting the admin's confirmation. Nothing is written to
  // the product until confirmReextract() runs.
  const [pendingExtraction, setPendingExtraction] = useState<Awaited<ReturnType<typeof extractSpecSheet>> | null>(null);
  const [savingExtraction, setSavingExtraction] = useState(false);
  const [consumerSpecsOpen, setConsumerSpecsOpen] = useState(false);
  const [installerSpecsOpen, setInstallerSpecsOpen] = useState(false);
  const specFileRef = useRef<HTMLInputElement>(null);

  // Section — Flame Anchor
  const [flameAnchorForm, setFlameAnchorForm] = useState({ x: "0", y: "0.15", z: "-0.05", scale: "0.3" });
  const [savingFlameAnchor, setSavingFlameAnchor] = useState(false);
  const [flameAnchorSaved, setFlameAnchorSaved] = useState(false);

  // Section 5 — Danger
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /* ---- Load product + manufacturers ---- */
  const loadProduct = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);

    try {
      const [prodRes, mfgRes, finishRes] = await Promise.all([
        fetch(
          `${SB_URL}/rest/v1/products?id=eq.${id}` +
            `&select=id,name,category,manufacturer_id,active,base_price,description,image_url,` +
            `asset_status,pbr_status,geometry_status,glb_assembly_status,qa_status,qa_flags,qa_render_url,` +
            `glb_pbr_url,usdz_pbr_url,spec_sheet_url,consumer_specs_json,install_specs_json,clearances,` +
            `flame_anchor,available_countries,` +
            `placement_type,geometry_source,material_source,finish_id,footprint_width_mm,footprint_depth_mm,` +
            `overall_height_mm,requires_open_sky,shadow_atlas_key,` +
            `created_at,updated_at,manufacturers(name)`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
        ),
        fetch(
          `${SB_URL}/rest/v1/manufacturers?select=id,name&order=name`,
          { headers: { apikey: ANON_KEY } }
        ),
        // finish_library: global finishes plus manufacturer-scoped ones (admins can read all).
        fetch(
          `${SB_URL}/rest/v1/finish_library?select=id,name,hex,category,manufacturer_id&active=eq.true&order=name`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
        ),
      ]);

      if (!prodRes.ok) throw new Error(`Product fetch failed (${prodRes.status})`);
      const [prod]: ProductData[] = await prodRes.json();
      if (!prod) { setNotFound(true); return; }

      // use_photo_texture is read on its own so this page still loads before migration 20261101000500 is applied.
      let usePhotoTexture: boolean | null = null;
      try {
        const ptRes = await fetch(
          `${SB_URL}/rest/v1/products?id=eq.${id}&select=use_photo_texture`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
        );
        if (ptRes.ok) usePhotoTexture = (await ptRes.json())[0]?.use_photo_texture ?? null;
      } catch { /* column not deployed yet: leave null (treated as off) */ }
      prod.use_photo_texture = usePhotoTexture;

      setProduct(prod);
      setArStatus(arDisplayStatus(prod));
      setFlameAnchorForm({
        x: String(prod.flame_anchor?.x ?? 0),
        y: String(prod.flame_anchor?.y ?? 0.15),
        z: String(prod.flame_anchor?.z ?? -0.05),
        scale: String(prod.flame_anchor?.scale ?? 0.3),
      });
      setInfoForm({
        name: prod.name,
        category: prod.category ?? "",
        manufacturer_id: prod.manufacturer_id ?? "",
        base_price: prod.base_price != null ? String(prod.base_price) : "",
        description: prod.description ?? "",
        active: prod.active,
        available_countries: prod.available_countries ?? null,
      });

      if (mfgRes.ok) setManufacturers(await mfgRes.json());
      if (finishRes.ok) setFinishes(await finishRes.json());
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Failed to load product" });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadProduct(); }, [loadProduct]);

  /* ---- AR status polling ---- */
  useEffect(() => {
    if (!id || id === 'undefined') return;
    if (arStatus === "ready" || arStatus === "failed") return;
    const interval = setInterval(async () => {
      if (!id || id === 'undefined') return;
      try {
        const res = await fetch(
          `${SB_URL}/rest/v1/products?id=eq.${id}&select=asset_status,glb_assembly_status,glb_pbr_url,usdz_pbr_url`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
        );
        if (res.ok) {
          const [row]: [{ asset_status: string | null; glb_assembly_status: string | null; glb_pbr_url: string | null; usdz_pbr_url: string | null }?] = await res.json();
          const effectiveStatus = row ? arDisplayStatus(row) : null;
          if (effectiveStatus && effectiveStatus !== arStatus) {
            setArStatus(effectiveStatus);
            if (effectiveStatus === "ready") toast({ title: "AR model ready", description: "GLB and USDZ processed." });
          }
        }
      } catch { /* silent */ }
    }, 8000);
    return () => clearInterval(interval);
  }, [id, arStatus]);

  /* ---- Section 1: Save Info ---- */
  async function saveInfo() {
    if (!product) return;
    setSavingInfo(true);
    try {
      const fields: Record<string, unknown> = {
        name: infoForm.name.trim(),
        category: infoForm.category,
        manufacturer_id: infoForm.manufacturer_id || null,
        active: infoForm.active,
      };
      if (infoForm.base_price) fields.base_price = Number(infoForm.base_price);
      if (infoForm.description !== undefined) fields.description = infoForm.description;
      fields.available_countries = infoForm.available_countries ?? null;

      await patchProduct(product.id, fields, jwt);
      setProduct((p) => p ? { ...p, ...fields, name: infoForm.name.trim() } : p);
      toast({ title: "Product info saved" });
    } catch (err) {
      toast({ title: "Save failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSavingInfo(false);
    }
  }

  /* ---- Section 3: Demo view count ---- */
  async function fetchDemoViewCount(productId: string) {
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/demo_events?select=id&event_type=eq.view&product_id=eq.${productId}`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, Prefer: "count=exact", Range: "0-0" } }
      );
      const range = res.headers.get("Content-Range") ?? "";
      const count = parseInt(range.split("/")[1] ?? "0", 10);
      setDemoViewCount(isNaN(count) ? 0 : count);
    } catch {
      setDemoViewCount(null);
    }
  }

  /* ---- Section 2: Media uploads ---- */
  async function uploadMedia(file: File, slotKey: string, contentType: string): Promise<string | null> {
    try {
      const s3Key = `products/${product!.id}/${slotKey}.${file.name.split(".").pop()}`;
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(s3Key, contentType, jwt);
      await xhrPut(uploadUrl, file, contentType, () => {});
      return publicUrl;
    } catch (err) {
      toast({ title: "Upload failed", description: err instanceof Error ? err.message : "Unknown error" });
      return null;
    }
  }

  async function handleHeroFile(file: File) {
    setHeroUploading(true);
    setHeroProgress(0);
    try {
      const s3Key = `products/${product!.id}/hero.${file.name.split(".").pop()}`;
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(s3Key, file.type || "image/jpeg", jwt);
      await xhrPut(uploadUrl, file, file.type || "image/jpeg", setHeroProgress);
      await patchProduct(product!.id, { image_url: publicUrl }, jwt);
      setProduct((p) => p ? { ...p, image_url: publicUrl } : p);
      toast({ title: "Hero image updated" });
    } catch (err) {
      toast({ title: "Upload failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setHeroUploading(false);
    }
  }

  async function handleTextureFile(key: TextureKey, file: File) {
    setTextureUploading(key);
    const url = await uploadMedia(file, `textures/${key}`, file.type || "image/png");
    if (url) toast({ title: `${key} map uploaded` });
    setTextureUploading(null);
  }

  /* ---- Section 3: Pipeline triggers ---- */
  async function retriggerPbr() {
    if (!product) return;
    setRetriggeringPbr(true);
    try {
      await patchProduct(product.id, { pbr_status: null, asset_status: "processing" }, jwt);
      const res = await fetch(`${SB_URL}/functions/v1/trigger-pbr-pipeline`, {
        method: "POST",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.id }),
      });
      if (!res.ok) throw new Error(`Trigger failed (${res.status})`);
      setProduct((p) => p ? { ...p, pbr_status: "pending", asset_status: "processing" } : p);
      setArStatus("processing");
      toast({ title: "PBR pipeline triggered" });
    } catch (err) {
      toast({ title: "Failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setRetriggeringPbr(false);
    }
  }

  async function retriggerGeo() {
    if (!product) return;
    setRetriggeringGeo(true);
    try {
      await patchProduct(product.id, { geometry_status: null }, jwt);
      const res = await fetch(`${SB_URL}/functions/v1/trigger-geometry-pipeline`, {
        method: "POST",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.id }),
      });
      if (!res.ok) throw new Error(`Trigger failed (${res.status})`);
      setProduct((p) => p ? { ...p, geometry_status: "pending" } : p);
      toast({ title: "Geometry pipeline triggered" });
    } catch (err) {
      toast({ title: "Failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setRetriggeringGeo(false);
    }
  }

  async function retriggerFull() {
    if (!product) return;
    setRetriggeringFull(true);
    try {
      // Reset all stage statuses to null so orchestrate-pipeline picks them up
      await patchProduct(product.id, {
        pbr_status: null,
        geometry_status: null,
        glb_assembly_status: null,
        qa_status: null,
        asset_status: "processing",
      }, jwt);
      const res = await fetch(`${SB_URL}/functions/v1/orchestrate-pipeline`, {
        method: "POST",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.id }),
      });
      if (!res.ok) throw new Error(`Orchestrate failed (${res.status})`);
      setProduct((p) => p ? {
        ...p,
        pbr_status: "pending",
        geometry_status: "pending",
        glb_assembly_status: null,
        qa_status: null,
        asset_status: "processing",
      } : p);
      setArStatus("processing");
      toast({ title: "Full pipeline triggered", description: "All stages will be re-processed." });
    } catch (err) {
      toast({ title: "Failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setRetriggeringFull(false);
    }
  }

  /* ---- Finish (swatch) + assembly re-run ---- */
  async function saveFinish(newFinishId: string | null) {
    if (!product || !isAdmin) return;
    const prev = { finish_id: product.finish_id, material_source: product.material_source };
    const patch = finishPatch(newFinishId, product.material_source);
    setSavingFinish(true);
    try {
      await patchProduct(product.id, patch, jwt);
      setProduct((p) => (p ? { ...p, ...patch } : p));
      toast({
        title: newFinishId ? "Finish set" : "Finish cleared",
        description: "Takes effect the next time GLB assembly runs. Use Re-run assembly.",
      });
    } catch (err) {
      setProduct((p) => (p ? { ...p, ...prev } : p));
      toast({ title: "Failed to save finish", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSavingFinish(false);
    }
  }

  async function saveUsePhotoTexture(next: boolean) {
    if (!product || !isAdmin) return;
    const prev = product.use_photo_texture;
    setSavingFinish(true);
    try {
      await patchProduct(product.id, { use_photo_texture: next }, jwt);
      setProduct((p) => (p ? { ...p, use_photo_texture: next } : p));
      toast({
        title: next ? "Photo texture on" : "Photo texture off",
        description: "Takes effect the next time GLB assembly runs. Use Re-run assembly.",
      });
    } catch (err) {
      setProduct((p) => (p ? { ...p, use_photo_texture: prev } : p));
      toast({ title: "Failed to save", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSavingFinish(false);
    }
  }

  async function rerunAssembly() {
    if (!product || !isAdmin) return;
    const prevStatus = product.glb_assembly_status;
    setReassembling(true);
    try {
      // trigger-glb-assembly sets the status to 'pending' itself; null just clears the old result.
      await patchProduct(product.id, { glb_assembly_status: null }, jwt);
      const res = await fetch(`${SB_URL}/functions/v1/trigger-glb-assembly`, {
        method: "POST",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.id }),
      });
      if (!res.ok) {
        // Put the previous status back so a refused trigger does not leave the product blank.
        await patchProduct(product.id, { glb_assembly_status: prevStatus }, jwt).catch(() => {});
        let detail = "";
        try { detail = (await res.json())?.error ?? ""; } catch { /* not JSON */ }
        throw new Error(detail || `Trigger failed (${res.status})`);
      }
      setProduct((p) => (p ? { ...p, glb_assembly_status: "pending" } : p));
      setShowAssemblyConfirm(false);
      toast({ title: "GLB assembly triggered", description: "QA runs automatically afterwards." });
    } catch (err) {
      toast({ title: "Re-run failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setReassembling(false);
    }
  }

  /* ---- Section 4: Spec sheet ---- */
  async function handleSpecFile(file: File) {
    if (!product) return;
    setSpecUploading(true);
    setSpecProgress(0);
    try {
      const s3Key = `specs/${product.id}/${file.name}`;
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(s3Key, "application/pdf", jwt);
      await xhrPut(uploadUrl, file, "application/pdf", setSpecProgress);
      await patchProduct(product.id, { spec_sheet_url: publicUrl }, jwt);
      setProduct((p) => p ? { ...p, spec_sheet_url: publicUrl } : p);
      toast({ title: "Spec sheet uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSpecUploading(false);
    }
  }

  // Step 1 of 2: run the extraction and open the review dialog. Writes nothing.
  async function reprocessSpecs() {
    if (!product?.spec_sheet_url) return;
    setSpecExtracting(true);
    try {
      const result = await extractSpecSheet(
        product.name,
        product.category ?? "Other",
        jwt,
        product.spec_sheet_url,
        (product as ProductData & { install_spec_sheet_url?: string }).install_spec_sheet_url ?? undefined
      );
      setPendingExtraction(result);
    } catch (err) {
      toast({ title: "Extraction failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSpecExtracting(false);
    }
  }

  // Step 2 of 2: write only what the admin confirmed. Clearances are MERGED into
  // the existing document (per-standard sets, other keys and non-accepted fields
  // are kept) — never replaced wholesale.
  async function confirmReextract(acceptedKeys: string[], refreshSpecs: boolean) {
    if (!product || !pendingExtraction) return;
    const { consumerSpecs, installerSpecs, clearances, partial, extractionWarning } = pendingExtraction;
    const patchFields: Record<string, unknown> = {};
    if (refreshSpecs) {
      patchFields.consumer_specs_json = consumerSpecs;
      patchFields.install_specs_json = installerSpecs;
    }
    let mergedClearances: Record<string, unknown> | null = null;
    if (clearances && acceptedKeys.length > 0) {
      mergedClearances = mergeClearances(product.clearances, clearances, acceptedKeys);
      patchFields.clearances = mergedClearances;
    }
    if (Object.keys(patchFields).length === 0) {
      setPendingExtraction(null);
      return;
    }
    setSavingExtraction(true);
    try {
      await patchProduct(product.id, patchFields, jwt);
      setProduct((p) => p ? {
        ...p,
        ...(refreshSpecs ? {
          consumer_specs_json: consumerSpecs as unknown as Record<string, unknown>,
          install_specs_json: installerSpecs as unknown as Record<string, unknown>,
        } : {}),
        ...(mergedClearances ? { clearances: mergedClearances } : {}),
      } : p);
      setPendingExtraction(null);
      if (partial) {
        toast({
          title: "Specs re-extracted with limited data",
          description: extractionWarning ?? "Spec sheet was too large to fully process — review and fill in missing fields.",
        });
      } else {
        toast({ title: "Specs re-extracted" });
      }
    } catch (err) {
      // Dialog stays open so the admin can retry or cancel; nothing was applied.
      toast({ title: "Saving re-extracted specs failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSavingExtraction(false);
    }
  }

  /* ---- Section: Flame Anchor ---- */
  async function saveFlameAnchor() {
    if (!product) return;
    setSavingFlameAnchor(true);
    try {
      const anchor = {
        x: parseFloat(flameAnchorForm.x) || 0,
        y: parseFloat(flameAnchorForm.y) || 0,
        z: parseFloat(flameAnchorForm.z) || 0,
        scale: parseFloat(flameAnchorForm.scale) || 0.3,
      };
      await patchProduct(product.id, { flame_anchor: anchor }, jwt);
      setProduct((p) => p ? { ...p, flame_anchor: anchor } : p);
      setFlameAnchorSaved(true);
      setTimeout(() => setFlameAnchorSaved(false), 2000);
    } catch (err) {
      toast({ title: "Save failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSavingFlameAnchor(false);
    }
  }

  async function clearFlameAnchor() {
    if (!product) return;
    setSavingFlameAnchor(true);
    try {
      await patchProduct(product.id, { flame_anchor: null }, jwt);
      setProduct((p) => p ? { ...p, flame_anchor: null } : p);
      setFlameAnchorForm({ x: "0", y: "0.15", z: "-0.05", scale: "0.3" });
      toast({ title: "Flame anchor cleared", description: "Bounding-box heuristic will be used." });
    } catch (err) {
      toast({ title: "Save failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSavingFlameAnchor(false);
    }
  }

  /* ---- Section 5: Danger ---- */
  async function deactivateProduct() {
    if (!product) return;
    setDeactivating(true);
    try {
      await patchProduct(product.id, { active: false }, jwt);
      setProduct((p) => p ? { ...p, active: false } : p);
      setInfoForm((f) => ({ ...f, active: false }));
      toast({ title: "Product deactivated" });
    } catch (err) {
      toast({ title: "Failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setDeactivating(false);
    }
  }

  async function deleteProduct() {
    if (!product) return;
    setDeleting(true);
    try {
      const headers = { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, Prefer: "return=minimal" };
      const res = await fetch(`${SB_URL}/rest/v1/products?id=eq.${product.id}`, { method: "DELETE", headers });
      if (!res.ok) {
        const text = await res.text();
        console.error("Delete error:", text.slice(0, 400));
        throw new Error(`Delete failed (${res.status}): ${text.slice(0, 200)}`);
      }
      toast({ title: "Product deleted" });
      navigate("/admin/products");
    } catch (err) {
      toast({ title: "Delete failed", description: err instanceof Error ? err.message : "Unknown error" });
      setDeleting(false);
      setShowDeleteModal(false);
    }
  }

  /* ---- Render ---- */
  if (loading) return <div className="max-w-2xl"><LoadingSkeleton /></div>;

  if (notFound) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/products" className="text-sm text-gray-500 hover:text-gray-300 transition">← Products</Link>
        <div className="rounded-xl border border-gray-800 py-14 text-center">
          <p className="text-gray-400">Product not found.</p>
        </div>
      </div>
    );
  }

  const consumerSpecs = product?.consumer_specs_json as ConsumerSpecs | null;
  const installerSpecs = product?.install_specs_json as unknown as InstallerSpecs | null;

  return (
    <div className="max-w-2xl space-y-5">
      {/* Header */}
      <div>
        <Link to="/admin/products" className="text-sm text-gray-500 hover:text-gray-300 transition">
          ← Products
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{product?.name ?? "Product"}</h1>
            {product?.updated_at && (
              <p className="mt-0.5 text-xs text-gray-500">
                Last updated {new Date(product.updated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ArStatusBadge status={arStatus} />
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${infoForm.active ? "border-green-800 bg-green-900/30 text-green-400" : "border-gray-700 bg-gray-800 text-gray-500"}`}>
              {infoForm.active ? "Active" : "Draft"}
            </span>
            {/* "View in App" — stub */}
            <button
              disabled
              title="Coming soon"
              className="rounded-lg border border-gray-700 px-3 py-1.5 text-xs text-gray-600 cursor-not-allowed"
            >
              View in App
            </button>
          </div>
        </div>
      </div>

      {/* ── Section 1: Product Info ── */}
      <Section title="Product Info" open={openSections.info} onToggle={() => toggleSection("info")}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <FieldLabel>Product Name</FieldLabel>
            <input
              type="text"
              value={infoForm.name}
              onChange={(e) => setInfoForm((f) => ({ ...f, name: e.target.value }))}
              style={inputStyle}
              className={inputCls}
            />
          </div>

          <div>
            <FieldLabel>Category</FieldLabel>
            <select
              value={infoForm.category}
              onChange={(e) => setInfoForm((f) => ({ ...f, category: e.target.value }))}
              style={inputStyle}
              className={inputCls}
            >
              <option value="">Select category…</option>
              {CATEGORY_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <FieldLabel>Manufacturer</FieldLabel>
            <select
              value={infoForm.manufacturer_id}
              onChange={(e) => setInfoForm((f) => ({ ...f, manufacturer_id: e.target.value }))}
              style={inputStyle}
              className={inputCls}
            >
              <option value="">Select manufacturer…</option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          <div>
            <FieldLabel>MSRP (USD)</FieldLabel>
            <input
              type="number"
              value={infoForm.base_price}
              onChange={(e) => setInfoForm((f) => ({ ...f, base_price: e.target.value }))}
              placeholder="0.00"
              style={inputStyle}
              className={inputCls}
            />
          </div>

          <div className="flex items-center gap-3 pt-4">
            <FieldLabel>Published</FieldLabel>
            <button
              onClick={() => setInfoForm((f) => ({ ...f, active: !f.active }))}
              className={[
                "relative inline-flex h-5 w-9 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                infoForm.active ? "bg-orange-600" : "bg-gray-700",
              ].join(" ")}
            >
              <span
                className={[
                  "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                  infoForm.active ? "translate-x-4" : "translate-x-0",
                ].join(" ")}
              />
            </button>
            <span className="text-sm text-gray-400">{infoForm.active ? "Active" : "Draft"}</span>
          </div>
        </div>

        <div>
          <FieldLabel>Description</FieldLabel>
          <textarea
            value={infoForm.description}
            onChange={(e) => setInfoForm((f) => ({ ...f, description: e.target.value }))}
            rows={3}
            placeholder="Product description…"
            style={inputStyle}
            className={inputCls + " resize-none"}
          />
        </div>

        <div>
          <FieldLabel>Available Countries</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {(
              [
                { code: null, label: "All Countries" },
                { code: "CA", label: "🇨🇦 Canada" },
                { code: "US", label: "🇺🇸 United States" },
              ] as { code: string | null; label: string }[]
            ).map(({ code, label }) => {
              const isAll = code === null;
              const active = isAll
                ? infoForm.available_countries === null
                : (infoForm.available_countries ?? []).includes(code!);
              return (
                <button
                  key={String(code)}
                  type="button"
                  onClick={() => {
                    if (isAll) {
                      setInfoForm((f) => ({ ...f, available_countries: null }));
                    } else {
                      setInfoForm((f) => {
                        const current = f.available_countries ?? [];
                        const next = current.includes(code!)
                          ? current.filter((c) => c !== code!)
                          : [...current, code!];
                        return { ...f, available_countries: next.length === 0 ? null : next };
                      });
                    }
                  }}
                  className={[
                    "rounded-lg border px-3 py-1.5 text-sm transition",
                    active
                      ? "border-teal-600 bg-teal-900/40 text-teal-300"
                      : "border-gray-700 bg-transparent text-gray-400 hover:border-gray-500",
                  ].join(" ")}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-gray-600">
            "All Countries" means no restriction. Select specific markets to limit mobile app visibility.
          </p>
        </div>

        <div className="flex justify-end">
          <SaveButton saving={savingInfo} onClick={saveInfo} />
        </div>
      </Section>

      {/* ── State Restrictions ── */}
      <Section title="State Restrictions" open={openSections.stateRestrictions} onToggle={() => toggleSection("stateRestrictions")}>
        {product && <StateRestrictionsSection productId={product.id} jwt={jwt} />}
      </Section>

      {/* ── Section 2: Media Assets ── */}
      <Section title="Media Assets" open={openSections.media} onToggle={() => toggleSection("media")}>
        {/* A parametric product has no photography behind it. The hero image
            still applies — a catalogue thumbnail is wanted either way — but the
            panel says so, rather than presenting empty slots that imply a
            missing upload someone ought to go and find. */}
        {isParametric && (
          <p className="mb-4 rounded-lg border border-gray-800 bg-gray-900/40 p-3 text-xs text-gray-400">
            This product was generated from parameters, not photographed. There are no source
            photos to upload; the hero image below is optional catalogue artwork only.
          </p>
        )}
        {/* Hero image */}
        <div>
          <FieldLabel>Hero Image</FieldLabel>
          <div className="flex items-center gap-4">
            {product?.image_url ? (
              <img
                src={product.image_url}
                alt="Hero"
                className="h-20 w-20 rounded-xl object-cover border border-gray-700"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-gray-800 bg-gray-800 text-2xl">
                📦
              </div>
            )}
            <div className="space-y-1.5">
              <button
                onClick={() => heroRef.current?.click()}
                disabled={heroUploading}
                className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
              >
                {heroUploading ? `Uploading ${heroProgress}%…` : product?.image_url ? "Replace image" : "Upload image"}
              </button>
              <p className="text-xs text-gray-600">JPG, PNG, or WebP</p>
            </div>
            <input
              ref={heroRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleHeroFile(f); }}
            />
          </div>
        </div>

        {/* Texture maps */}
        <div>
          <FieldLabel>Texture Maps</FieldLabel>
          <div className="grid grid-cols-2 gap-3">
            {TEXTURE_MAPS.map(({ key, label, tip }) => (
              <div key={key} className="rounded-xl border border-gray-800 bg-gray-900/60 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-medium text-gray-300">{label}</span>
                    <span title={tip} className="cursor-help text-gray-600 hover:text-gray-400 text-xs">ⓘ</span>
                  </div>
                  {textureUploading === key && (
                    <span className="text-xs text-orange-400">Uploading…</span>
                  )}
                </div>
                <button
                  onClick={() => textureRefs.current[key]?.click()}
                  disabled={textureUploading !== null}
                  className="w-full rounded-lg border border-dashed border-gray-700 py-2 text-xs text-gray-500 hover:border-gray-500 hover:text-gray-300 disabled:opacity-40 transition"
                >
                  Upload PNG
                </button>
                <input
                  ref={(el) => { textureRefs.current[key] = el; }}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleTextureFile(key, f); }}
                />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Section 3: 3D Model & AR ── */}
      <Section title="3D Model & AR" open={openSections.ar} onToggle={() => toggleSection("ar")}>
        {/* Pipeline stage badges */}
        {(() => {
          const STAGE_META: Record<string, { cls: string; dot: string }> = {
            complete:   { cls: "text-green-400 border-green-800/50 bg-green-900/20",   dot: "bg-green-500" },
            approved:   { cls: "text-green-400 border-green-800/50 bg-green-900/20",   dot: "bg-green-500" },
            processing: { cls: "text-blue-400 border-blue-800/50 bg-blue-900/20",      dot: "bg-blue-500 animate-pulse" },
            pending:    { cls: "text-yellow-400 border-yellow-800/50 bg-yellow-900/20", dot: "bg-yellow-500" },
            failed:     { cls: "text-red-400 border-red-800/50 bg-red-900/20",         dot: "bg-red-500" },
            review:     { cls: "text-amber-400 border-amber-800/50 bg-amber-900/20",   dot: "bg-amber-500" },
            rejected:   { cls: "text-red-400 border-red-800/50 bg-red-900/20",         dot: "bg-red-500" },
            skipped:    { cls: "text-gray-500 border-gray-700 bg-gray-800/60",         dot: "bg-gray-600" },
          };
          const stages = [
            { label: "PBR Materials",  status: product?.pbr_status ?? null },
            { label: "Geometry",       status: product?.geometry_status ?? null },
            { label: "GLB Assembly",   status: product?.glb_assembly_status ?? null },
            { label: "QA Validation",  status: product?.qa_status ?? null },
          ];
          return (
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pipeline Stages</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {stages.map(({ label, status }) => {
                  const m = STAGE_META[status ?? ""] ?? { cls: "text-gray-600 border-gray-800 bg-gray-900/40", dot: "bg-gray-700" };
                  return (
                    <div key={label} className={`rounded-lg border px-3 py-2 ${m.cls}`}>
                      <p className="text-xs text-gray-500 mb-1">{label}</p>
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${m.dot}`} />
                        <span className="text-xs font-medium capitalize">{status ?? "—"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* QA flags — humanized */}
        {product?.qa_flags && (product.qa_flags as string[]).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-gray-500">QA Flags:</span>
            {(product.qa_flags as string[]).map((flag) => {
              const FLAG_LABELS: Record<string, string> = {
                "render_mode:software_v1": "Scored against real 3D render",
                "ssim_self_reference":     "SSIM self-reference",
                "ai_error":                "AI check failed",
                "no_source_photos":        "No source photos",
              };
              const label = FLAG_LABELS[flag] ?? (flag.startsWith("render_unavailable:") ? "Render unavailable (no service)" : flag.replace(/_/g, " "));
              return (
                <span key={flag} className="rounded border border-amber-800/40 bg-amber-900/20 px-1.5 py-0.5 text-xs text-amber-400">
                  {label}
                </span>
              );
            })}
          </div>
        )}

        {/* QA render thumbnail */}
        {product?.qa_render_url && (
          <div className="flex items-center gap-3">
            <img
              src={product.qa_render_url}
              alt="QA render"
              className="h-14 w-14 rounded-lg border border-gray-700 object-contain bg-gray-900 shrink-0"
            />
            <div className="space-y-0.5">
              <p className="text-xs text-gray-500">QA render available</p>
              <a
                href="/admin/pipeline"
                className="text-xs text-orange-400 hover:text-orange-300 underline underline-offset-2"
              >
                Review in Pipeline
              </a>
            </div>
          </div>
        )}

        {/* Overall AR status */}
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-400">AR status:</p>
          <ArStatusBadge status={arStatus} />
        </div>

        {(arStatus === "processing" || arStatus === "pending") && (
          <p className="text-xs text-gray-600">Auto-polling every 8 seconds…</p>
        )}
        {arStatus === "ready" && (
          <p className="text-xs text-green-400">GLB and USDZ are ready for AR placement.</p>
        )}
        {arStatus === "failed" && (
          <p className="text-xs text-red-400">Pipeline failed — check individual stage statuses above.</p>
        )}

        {/* Finish (swatch) — admin only. Sets finish_id + material_source = 'swatch_library'. */}
        {isAdmin && (
          <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-3 space-y-2">
            <label htmlFor="finish-select" className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Finish
            </label>
            <select
              id="finish-select"
              value={product?.finish_id ?? ""}
              disabled={savingFinish || !product}
              onChange={(e) => void saveFinish(e.target.value || null)}
              className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 disabled:opacity-50"
            >
              <option value="">None (use the pipeline's own material)</option>
              {finishes.map((f) => (
                <option key={f.id} value={f.id}>{finishOptionLabel(f)}</option>
              ))}
              {product?.finish_id && !finishes.some((f) => f.id === product.finish_id) && (
                <option value={product.finish_id}>(finish not in the list — {product.finish_id.slice(0, 8)})</option>
              )}
            </select>
            {(() => {
              const warning = product ? swatchModeWarning(product.geometry_source, product.material_source) : null;
              return warning ? (
                <p className="text-xs text-amber-400 rounded-lg border border-amber-800/50 bg-amber-900/20 px-3 py-2">{warning}</p>
              ) : null;
            })()}
            <p className="text-xs text-gray-500">
              Choosing a finish sets the product to swatch mode; clearing it reverts. The change applies at the next GLB assembly.
            </p>
            {product && (product.geometry_source === "dxf_extrusion" || product.geometry_source === "rfa_cad") && (
              <label className="flex items-start gap-2 pt-1 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={product.use_photo_texture === true}
                  disabled={savingFinish}
                  onChange={(e) => void saveUsePhotoTexture(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Use photo texture
                  <span className="block text-xs text-gray-500">
                    CAD meshes normally ship flat (the stored photo is a room scene and does not map to CAD UVs).
                    Tick only after you have checked the photo texture looks right on this product, then re-run assembly.
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        {/* Re-trigger buttons */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            onClick={retriggerPbr}
            disabled={retriggeringPbr || retriggeringFull}
            className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
          >
            {retriggeringPbr ? "Triggering…" : "Re-run PBR"}
          </button>
          <button
            onClick={retriggerGeo}
            disabled={retriggeringGeo || retriggeringFull}
            className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
          >
            {retriggeringGeo ? "Triggering…" : "Re-run Geometry"}
          </button>
          {isAdmin && (() => {
            const gate = product ? assemblyRerun(product) : { allowed: false, reason: null };
            return (
              <button
                onClick={() => setShowAssemblyConfirm(true)}
                disabled={!gate.allowed || reassembling || retriggeringFull || retriggeringPbr || retriggeringGeo}
                title={gate.reason ?? "Re-run GLB assembly only (keeps PBR and geometry results)"}
                className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
              >
                {reassembling ? "Triggering…" : "Re-run Assembly"}
              </button>
            );
          })()}
          <button
            onClick={retriggerFull}
            disabled={retriggeringFull || retriggeringPbr || retriggeringGeo}
            className="rounded-lg bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 transition"
          >
            {retriggeringFull ? "Triggering…" : "Re-run Full Pipeline"}
          </button>
        </div>

        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-3 space-y-2">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Product ID (for pipeline reference)</p>
          <CopyableUrl label="ID" url={product?.id ?? ""} />
        </div>

        {/* ── Demo link generator ── */}
        {product && (
          <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Demo Link</p>
                {demoViewCount !== null && (
                  <p className="text-xs text-gray-600">
                    {demoViewCount} demo view{demoViewCount !== 1 ? "s" : ""}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setDemoModalOpen(true);
                  fetchDemoViewCount(product.id);
                }}
                className="rounded-lg border border-amber-700/50 bg-amber-600/10 px-3 py-1.5 text-sm text-amber-400 hover:bg-amber-600/20 transition"
              >
                Generate Demo Link
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* ── Demo link modal ── */}
      {demoModalOpen && product && (() => {
        const demoUrl = `https://www.fyrris.com/demo/${product.id}?utm_source=demo&utm_medium=admin&utm_campaign=sales`;
        return (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4"
            // Close only when the gesture both STARTS and ends on the
            // backdrop itself — not on `click`, which fires wherever the
            // mouse-UP lands. A click-based handler here closes the modal
            // the moment someone tries to drag-select the URL text to copy
            // it: the mousedown starts inside the modal, the drag ends
            // outside the (fairly narrow, max-w-sm) modal bounds, and the
            // resulting click fires directly on the backdrop — never
            // touching the inner content's stopPropagation at all, since
            // that only stops a click whose mousedown AND mouseup both
            // happened inside. Matches this bug exactly: "disappears
            // before it can be read or copied."
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setDemoModalOpen(false);
            }}
          >
            <div
              className="bg-gray-900 rounded-2xl border border-gray-700 p-6 w-full max-w-sm space-y-4 max-h-[92dvh] overflow-y-auto"
              // Stops a press that STARTS inside the modal from ever
              // reaching the backdrop's onMouseDown above, regardless of
              // where the drag ends — the actual fix. onClick's
              // stopPropagation is kept too (harmless, and still correct
              // for a plain click that starts and ends inside).
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-white">Demo Link</h3>
                <button
                  onClick={() => setDemoModalOpen(false)}
                  className="text-gray-500 hover:text-gray-300 transition text-lg leading-none"
                >
                  ✕
                </button>
              </div>

              {/* QR code — api.qrserver.com (no package needed) */}
              <div className="flex flex-col items-center gap-2">
                <div className="bg-white rounded-xl p-3">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(demoUrl)}&size=220x220&format=png`}
                    alt="QR Code"
                    width={220}
                    height={220}
                  />
                </div>
                <p className="text-xs text-gray-500">Screenshot and share</p>
              </div>

              {/* URL display */}
              <div className="rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-xs font-mono text-gray-400 break-all leading-relaxed">
                {demoUrl}
              </div>

              {/* Copy button */}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(demoUrl).then(() => {
                    setDemoCopied(true);
                    setTimeout(() => setDemoCopied(false), 2000);
                  });
                }}
                className="w-full rounded-lg bg-amber-600 hover:bg-amber-500 active:bg-amber-700 text-white py-2.5 text-sm font-semibold transition"
              >
                {demoCopied ? "✓ Copied to clipboard!" : "Copy Link"}
              </button>

              {demoViewCount !== null && (
                <p className="text-center text-xs text-gray-600">
                  {demoViewCount} view{demoViewCount !== 1 ? "s" : ""} so far
                </p>
              )}

              {/* Live preview iframe */}
              <div className="rounded-xl border border-gray-800 overflow-hidden" style={{ height: 380 }}>
                <iframe
                  src={`/demo/${product.id}`}
                  title="Demo preview"
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Section 4: Spec Sheet ── */}
      <Section title="Spec Sheet & AI Specs" open={openSections.specs} onToggle={() => toggleSection("specs")}>
        {/* Current spec sheet */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm text-gray-300">Spec Sheet PDF</p>
            {product?.spec_sheet_url ? (
              <a
                href={product.spec_sheet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-orange-400 hover:text-orange-300 underline underline-offset-2"
              >
                View PDF
              </a>
            ) : (
              <p className="text-xs text-gray-600">No spec sheet uploaded</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {product?.spec_sheet_url && (
              <button
                onClick={reprocessSpecs}
                disabled={specExtracting}
                className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
              >
                {specExtracting ? "Claude is reading…" : "Re-process with Claude"}
              </button>
            )}
            <button
              onClick={() => specFileRef.current?.click()}
              disabled={specUploading}
              className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
            >
              {specUploading ? `Uploading ${specProgress}%…` : product?.spec_sheet_url ? "Replace PDF" : "Upload PDF"}
            </button>
          </div>
        </div>
        <input
          ref={specFileRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSpecFile(f); }}
        />

        {specExtracting && (
          <div className="rounded-lg border border-blue-800 bg-blue-900/20 px-4 py-3 text-sm text-blue-300">
            ✦ Claude is reading the spec sheet…
          </div>
        )}

        {/* Consumer specs collapsible */}
        {consumerSpecs && (
          <div className="rounded-xl border border-gray-800 overflow-hidden">
            <button
              onClick={() => setConsumerSpecsOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-300 hover:bg-gray-800/40 transition"
            >
              <span className="font-medium">Consumer Specs <span className="text-xs text-green-400 ml-1">✓ extracted</span></span>
              <span className="text-xs text-gray-500">{consumerSpecsOpen ? "▲" : "▼"}</span>
            </button>
            {consumerSpecsOpen && (
              <div className="px-4 pb-4 border-t border-gray-800 space-y-2 pt-3">
                {consumerSpecs.dimensions && (
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {(["height", "width", "depth"] as const).map((d) => (
                      <div key={d} className="flex flex-col">
                        <span className="text-gray-500 capitalize">{d}</span>
                        <span className="text-gray-200">{(consumerSpecs.dimensions as Record<string, string>)[d] ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                )}
                {([
                  ["Weight",    consumerSpecs.weight],
                  ["BTU",       consumerSpecs.btu_output],
                  ["Fuel",      consumerSpecs.fuel_type],
                  ["Warranty",  consumerSpecs.warranty],
                ] as [string, string | null][]).filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex justify-between text-xs border-b border-gray-800 pb-1">
                    <span className="text-gray-500">{label}</span>
                    <span className="text-gray-200">{value}</span>
                  </div>
                ))}
                {consumerSpecs.key_features && consumerSpecs.key_features.length > 0 && (
                  <ul className="space-y-1 pt-1">
                    {consumerSpecs.key_features.map((f, i) => (
                      <li key={i} className="text-xs text-gray-300 flex gap-2">
                        <span className="text-orange-500 shrink-0">•</span>{f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* Installer specs collapsible (admin only) */}
        {installerSpecs && (
          <div className="rounded-xl border border-gray-800 overflow-hidden">
            <button
              onClick={() => setInstallerSpecsOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-300 hover:bg-gray-800/40 transition"
            >
              <span className="font-medium">
                Installer Specs
                <span className="ml-2 text-xs text-gray-600 border border-gray-700 rounded px-1">Admin only</span>
              </span>
              <span className="text-xs text-gray-500">{installerSpecsOpen ? "▲" : "▼"}</span>
            </button>
            {installerSpecsOpen && (
              <div className="px-4 pb-4 border-t border-gray-800 space-y-2 pt-3">
                {([
                  ["Clearance",          installerSpecs.clearance_requirements],
                  ["Venting",            installerSpecs.venting_type],
                  ["Gas Line",           installerSpecs.gas_line_specs],
                  ["Electrical",         installerSpecs.electrical_requirements],
                  ["Framing",            installerSpecs.framing_requirements],
                  ["Install Time",       installerSpecs.estimated_install_time],
                ] as [string, string | null][]).filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex justify-between text-xs border-b border-gray-800 pb-1">
                    <span className="text-gray-500">{label}</span>
                    <span className="text-gray-200 text-right max-w-[60%]">{value}</span>
                  </div>
                ))}
                {installerSpecs.installation_notes && installerSpecs.installation_notes.length > 0 && (
                  <ul className="space-y-1">
                    {installerSpecs.installation_notes.map((n, i) => (
                      <li key={i} className="text-xs text-gray-300 flex gap-2">
                        <span className="text-blue-500 shrink-0">•</span>{n}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ── Parametric source (M5b) ── */}
      {isParametric && (
        <Section title="Parametric Source" open={openSections.parametric} onToggle={() => toggleSection("parametric")}>
          <ParametricSummary product={product} />
        </Section>
      )}

      {/* ── Section: Safety Clearances ── */}
      {/* Hearth-only. NFPA 211 / CSA B365 clearances do not apply to a ground
          structure; its outdoor advisory set is M6. Rendering an empty clearance
          form for a pergola invites someone to fill it in. */}
      {!isGroundStructure && (
      <Section title="Safety Clearances" open={openSections.clearances} onToggle={() => toggleSection("clearances")}>
        {(() => {
          const cl = product?.clearances ?? null;
          const DIRS: [string, string][] = [
            ["Rear",    "rear_mm"],
            ["Side",    "side_mm"],
            ["Front",   "front_mm"],
            ["Ceiling", "ceiling_mm"],
            ["Floor",   "floor_mm"],
          ];
          const hasAnyValue = cl ? DIRS.some(([, key]) => cl[key] != null) : false;
          return (
            <div className="space-y-4">
              {cl && hasAnyValue ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Direction</th>
                      <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DIRS.map(([label, key]) => {
                      const mm = (cl[key] as number | null | undefined) ?? null;
                      const inches = mm != null ? (mm / 25.4).toFixed(1) : null;
                      // Manufacturer's printed value (clearances.original), shown
                      // verbatim; the converted inches only when there is none.
                      const orig = clearanceOriginal(cl, key);
                      return (
                        <tr key={key} className="border-b border-gray-800/50">
                          <td className="py-2.5 text-gray-400">{label}</td>
                          <td className="py-2.5">
                            {mm != null ? (
                              <span className="text-gray-200">
                                {mm} mm
                                {orig ? (
                                  <span className="ml-2 text-xs text-teal-400" title="Value and unit as printed on the spec sheet">
                                    printed: {formatClearanceOriginal(orig)}
                                  </span>
                                ) : (
                                  <span className="ml-2 text-xs text-gray-500">({inches} in)</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-500">
                  {cl ? "No clearance values were extracted from the spec sheet." : "No clearance data available for this product."}
                </p>
              )}
              {product?.spec_sheet_url && (
                <div className="flex items-center justify-between pt-1 gap-4">
                  <p className="text-xs text-gray-600">Clearances are auto-extracted from the spec sheet by Claude.</p>
                  <button
                    onClick={reprocessSpecs}
                    disabled={specExtracting}
                    className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition shrink-0"
                  >
                    {specExtracting ? "Extracting…" : "Re-extract from spec sheet"}
                  </button>
                </div>
              )}
              {!product?.spec_sheet_url && (
                <p className="text-xs text-gray-600">Upload a spec sheet to enable clearance extraction.</p>
              )}
            </div>
          );
        })()}
      </Section>
      )}

      {/* ── Flame Anchor ── */}
      {/* Hearth-only for the same reason as clearances — a pergola has no flame. */}
      {!isGroundStructure && (
      <Section title="Flame Anchor" open={openSections.flameAnchor} onToggle={() => toggleSection("flameAnchor")}>
        <p className="text-xs text-gray-500">
          Sets the position of the flame effect overlay in AR. Offsets are in metres from the model origin.
          Leave unset to use the automatic bounding-box heuristic.
        </p>

        {/* Live side-view diagram */}
        {(() => {
          const dotX = Math.max(30, Math.min(170, 100 + (parseFloat(flameAnchorForm.x) || 0) * 60));
          const dotY = Math.max(20, Math.min(120, 70 - (parseFloat(flameAnchorForm.y) || 0) * 60));
          return (
            <div className="flex justify-center">
              <svg width="200" height="140" style={{ background: "#0f172a", borderRadius: 8, display: "block" }}>
                {/* Product body */}
                <rect x="25" y="15" width="150" height="110" rx="6" fill="none" stroke="#374151" strokeWidth="2" />
                {/* Centre guides */}
                <line x1="100" y1="15" x2="100" y2="125" stroke="#1f2937" strokeWidth="1" />
                <line x1="25" y1="70" x2="175" y2="70" stroke="#1f2937" strokeWidth="1" />
                {/* Anchor crosshair */}
                <line x1={dotX - 9} y1={dotY} x2={dotX + 9} y2={dotY} stroke="#f97316" strokeWidth="1.5" />
                <line x1={dotX} y1={dotY - 9} x2={dotX} y2={dotY + 9} stroke="#f97316" strokeWidth="1.5" />
                <circle cx={dotX} cy={dotY} r="5" fill="#f97316" fillOpacity="0.85" />
                {/* Axis labels */}
                <text x="100" y="137" textAnchor="middle" fontSize="9" fill="#4b5563">X axis (left / right)</text>
                <text x="8" y="74" textAnchor="middle" fontSize="9" fill="#4b5563" transform="rotate(-90 8 74)">Y (up)</text>
              </svg>
            </div>
          );
        })()}

        {/* Inputs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {(
            [
              { key: "x" as const, label: "X offset (m)", hint: "Left / right from centre" },
              { key: "y" as const, label: "Y offset (m)", hint: "Up from model base" },
              { key: "z" as const, label: "Z offset (m)", hint: "Forward / back (negative = into wall)" },
              { key: "scale" as const, label: "Scale (m)", hint: "Flame plane size" },
            ]
          ).map(({ key, label, hint }) => (
            <div key={key}>
              <FieldLabel>{label}</FieldLabel>
              <input
                type="number"
                step="0.01"
                value={flameAnchorForm[key]}
                onChange={(e) => setFlameAnchorForm((f) => ({ ...f, [key]: e.target.value }))}
                style={inputStyle}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-gray-600">{hint}</p>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={saveFlameAnchor}
            disabled={savingFlameAnchor}
            className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 transition"
          >
            {savingFlameAnchor ? "Saving…" : "Save Anchor"}
          </button>
          <button
            onClick={clearFlameAnchor}
            disabled={savingFlameAnchor}
            className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
          >
            Clear Anchor (use heuristic)
          </button>
          {flameAnchorSaved && (
            <span className="text-xs font-medium text-green-400">Saved ✓</span>
          )}
        </div>

        {/* Current saved value */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Saved value</p>
          <pre className="rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-2 text-xs text-gray-400 font-mono overflow-x-auto">
            {product?.flame_anchor ? JSON.stringify(product.flame_anchor, null, 2) : "null (heuristic)"}
          </pre>
        </div>
      </Section>
      )}

      {/* ── Delete Product ── */}
      <div className="flex justify-end pt-2">
        <button
          onClick={() => setShowDeleteModal(true)}
          className="rounded-lg border border-red-800 px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 transition"
        >
          Delete Product
        </button>
      </div>

      {/* ── Section 5: Danger Zone ── */}
      <Section title="Danger Zone" open={openSections.danger} onToggle={() => toggleSection("danger")}>
        <div className="space-y-3">
          {/* Deactivate */}
          <div className="flex items-center justify-between rounded-xl border border-gray-800 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-gray-200">Deactivate Product</p>
              <p className="text-xs text-gray-500">Sets active = false. Product will be hidden from the app.</p>
            </div>
            <button
              onClick={deactivateProduct}
              disabled={deactivating || !infoForm.active}
              className="rounded-lg border border-yellow-800 px-3 py-1.5 text-sm text-yellow-400 hover:bg-yellow-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {deactivating ? "Deactivating…" : infoForm.active ? "Deactivate" : "Already inactive"}
            </button>
          </div>

          {/* Delete */}
          <div className="flex items-center justify-between rounded-xl border border-red-900/40 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-red-400">Delete Product</p>
              <p className="text-xs text-gray-500">Permanently deletes this product record. Cannot be undone.</p>
            </div>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="rounded-lg border border-red-800 px-3 py-1.5 text-sm text-red-400 hover:bg-red-900/20 transition"
              >
                Delete
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-400">Are you sure?</span>
                <button
                  onClick={deleteProduct}
                  disabled={deleting}
                  className="rounded-lg bg-red-700 px-3 py-1.5 text-sm text-white hover:bg-red-600 disabled:opacity-40 transition"
                >
                  {deleting ? "Deleting…" : "Yes, delete"}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800 transition"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </Section>

      {pendingExtraction && product && (
        <ClearanceReextractDialog
          rows={diffClearances(product.clearances, pendingExtraction.clearances)}
          hasExtractedClearances={!!pendingExtraction.clearances}
          hasStandards={
            !!product.clearances &&
            typeof product.clearances.standards === "object" &&
            product.clearances.standards !== null
          }
          partialWarning={pendingExtraction.partial ? (pendingExtraction.extractionWarning ?? "Spec sheet was too large to fully process — some fields may be missing.") : null}
          saving={savingExtraction}
          onConfirm={confirmReextract}
          onCancel={() => setPendingExtraction(null)}
        />
      )}

      {showAssemblyConfirm && (
        <DeleteConfirmDialog
          title={`Re-run GLB assembly for "${product?.name}"?`}
          description={
            "This rebuilds the GLB and USDZ and replaces the current files; the previous ones are not kept. " +
            "QA then runs again and its current result is replaced. " +
            (product?.finish_id ? "The selected finish will be applied." : "No finish is selected, so the pipeline's own material is used.")
          }
          confirmLabel="Re-run assembly"
          loadingLabel="Triggering…"
          loading={reassembling}
          onConfirm={rerunAssembly}
          onCancel={() => setShowAssemblyConfirm(false)}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmDialog
          title={`Delete "${product?.name}"?`}
          description="This will permanently delete this product and all related assets. This cannot be undone."
          loading={deleting}
          onConfirm={deleteProduct}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
